import React, { useState, useEffect } from "react";
import { useSnackbar } from "../contexts/SnackbarContext";
import * as deletionsLib from "../lib/deletionsLib";
import { Tab, TabGroup, TabList, TabPanels } from "@headlessui/react";

const Admin: React.FC = () => {
  const { showSnackbar } = useSnackbar();
  const [deletions, setDeletions] = useState<deletionsLib.QueuedDeletion[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [processingOverdue, setProcessingOverdue] = useState(false);

  useEffect(() => {
    loadDeletions();
  }, []);

  const loadDeletions = async () => {
    try {
      setLoading(true);
      const data = await deletionsLib.listQueuedDeletions();
      setDeletions(data);
    } catch (error) {
      console.error("Error loading deletions:", error);
      showSnackbar("Failed to load deletion queue", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteDeletion = async (deletionId: string) => {
    if (
      !window.confirm(
        "Are you sure you want to execute this deletion immediately? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      setProcessingId(deletionId);
      await deletionsLib.executeDeletion(deletionId);
      showSnackbar("Deletion executed successfully", "success");
      // Reload the list
      await loadDeletions();
    } catch (error) {
      console.error("Error executing deletion:", error);
      showSnackbar("Failed to execute deletion", "error");
    } finally {
      setProcessingId(null);
    }
  };

  const handleProcessOverdue = async () => {
    if (
      !window.confirm(
        "Are you sure you want to process all overdue deletions? This will permanently delete all items scheduled for deletion before now."
      )
    ) {
      return;
    }

    try {
      setProcessingOverdue(true);
      const result = await deletionsLib.processOverdueDeletions();
      showSnackbar(
        `Processed ${result.processedCount} deletion(s). Failed: ${result.failedCount}`,
        result.failedCount > 0 ? "warning" : "success"
      );
      // Reload the list
      await loadDeletions();
    } catch (error) {
      console.error("Error processing overdue deletions:", error);
      showSnackbar("Failed to process overdue deletions", "error");
    } finally {
      setProcessingOverdue(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const isPastDue = (scheduledFor: string) => {
    return new Date(scheduledFor) < new Date();
  };

  const pendingDeletions = deletions.filter((d) => d.status === "scheduled");
  const executedDeletions = deletions.filter(
    (d) => d.status === "executed" || d.status === "failed"
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <h1 className="text-2xl font-bold mb-6 dark:text-white">Admin Panel</h1>

      <TabGroup>
        <TabList className="flex space-x-1 rounded-xl bg-blue-900/20 p-1 mb-6">
          <Tab
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5
              ${
                selected
                  ? "bg-white text-blue-700 shadow dark:bg-gray-800 dark:text-white"
                  : "text-blue-100 hover:bg-white/[0.12] hover:text-white"
              }`
            }
          >
            Deletion Queue
          </Tab>
        </TabList>

        <TabPanels>
          <Tab.Panel>
            <div className="rounded-xl bg-white dark:bg-gray-800 p-6 shadow-lg">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold dark:text-white">
                  Deletion Queue Management
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={handleProcessOverdue}
                    disabled={processingOverdue}
                    className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {processingOverdue
                      ? "Processing..."
                      : "Process Overdue Deletions"}
                  </button>
                  <button
                    onClick={loadDeletions}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Refresh
                  </button>
                </div>
              </div>

              {/* Pending Deletions */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-3 dark:text-white">
                  Pending Deletions ({pendingDeletions.length})
                </h3>
                {pendingDeletions.length === 0 ? (
                  <p className="text-gray-600 dark:text-gray-400">
                    No pending deletions
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Resource
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Scheduled For
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {pendingDeletions.map((deletion) => (
                          <tr key={deletion.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  deletion.resourceType === "user"
                                    ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300"
                                    : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                                }`}
                              >
                                {deletion.resourceType}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                              <div className="font-medium">
                                {deletion.resourceName}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {deletion.resourceEmail}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                              {formatDate(deletion.scheduledFor)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {isPastDue(deletion.scheduledFor) ? (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
                                  Overdue
                                </span>
                              ) : (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                                  Scheduled
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <button
                                onClick={() =>
                                  handleExecuteDeletion(deletion.id)
                                }
                                disabled={processingId === deletion.id}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {processingId === deletion.id
                                  ? "Executing..."
                                  : "Execute Now"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Executed Deletions */}
              <div>
                <h3 className="text-lg font-semibold mb-3 dark:text-white">
                  Recently Executed ({executedDeletions.length})
                </h3>
                {executedDeletions.length === 0 ? (
                  <p className="text-gray-600 dark:text-gray-400">
                    No executed deletions
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Resource
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Was Scheduled For
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Executed At
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {executedDeletions.map((deletion) => (
                          <tr key={deletion.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  deletion.resourceType === "user"
                                    ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300"
                                    : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                                }`}
                              >
                                {deletion.resourceType}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                              <div className="font-medium">
                                {deletion.resourceName}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {deletion.resourceEmail}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                              {formatDate(deletion.scheduledFor)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                              {deletion.executedAt
                                ? formatDate(deletion.executedAt)
                                : "N/A"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  deletion.status === "executed"
                                    ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                                    : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                                }`}
                              >
                                {deletion.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </Tab.Panel>
        </TabPanels>
      </TabGroup>
    </div>
  );
};

export default Admin;
